import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, Platform } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useNavigation } from '@react-navigation/native';
import { Student } from '../../types';
import { EditStudentProfileModal } from './EditStudentProfileModal';
import { Colors, FontSize, Spacing, BorderRadius } from '../../theme/colors';

type Step = 'intro' | 'form';

interface Props {
  visible: boolean;
  student: Student | null;
  onSaved: () => void;
}

export function ProfileOnboardingModal({ visible, student, onSaved }: Props) {
  const [step, setStep] = useState<Step>('intro');
  const [isHidden, setIsHidden] = useState(false);
  const navigation = useNavigation<any>();

  useEffect(() => {
    if (visible) {
      setStep('intro');
      setIsHidden(false);
    }
  }, [visible]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      if (visible) {
        setIsHidden(false);
        setStep('intro');
      }
    });
    return unsubscribe;
  }, [navigation, visible]);

  if (!student || isHidden) return null;

  if (step === 'intro') {
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={() => {}} statusBarTranslucent>
        <View style={styles.overlay}>
          <View style={styles.card}>
            <View style={styles.introContainer}>
              <View style={styles.introIconWrapper}>
                <FeatherIcon name="file-text" size={48} color={Colors.primary} />
              </View>
              <Text style={styles.introTitle}>Payment Plan Set!</Text>
              <Text style={styles.introBody}>
                Your payment plan is set. As a final step, please complete {student.name}'s profile to ensure we have the correct emergency and medical information on file.
              </Text>
              <TouchableOpacity style={styles.primaryButton} onPress={() => {
                if (Platform.OS === 'web') {
                  setStep('form');
                } else {
                  setIsHidden(true);
                  navigation.navigate('EditProfile');
                }
              }}>
                <Text style={styles.primaryButtonText}>Complete Profile</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <EditStudentProfileModal
      visible={visible}
      onClose={() => {}}
      studentData={student}
      onSuccess={onSaved}
    />
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.lg,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: Colors.surface,
    borderRadius: BorderRadius.lg,
    overflow: 'hidden',
  },
  introContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: Spacing.xxl,
    paddingHorizontal: Spacing.xl,
  },
  introIconWrapper: {
    width: 88,
    height: 88,
    borderRadius: BorderRadius.full,
    backgroundColor: Colors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: Spacing.lg,
  },
  introTitle: {
    fontSize: FontSize.xxl,
    fontWeight: '700',
    color: Colors.text,
    marginBottom: Spacing.sm,
    textAlign: 'center',
  },
  introBody: {
    fontSize: FontSize.md,
    color: Colors.textSecondary,
    textAlign: 'center',
    marginBottom: Spacing.xl,
    lineHeight: 22,
  },
  primaryButton: {
    backgroundColor: Colors.primary,
    borderRadius: BorderRadius.md,
    paddingVertical: 14,
    paddingHorizontal: Spacing.xl,
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  primaryButtonText: {
    color: Colors.textInverse,
    fontSize: FontSize.lg,
    fontWeight: '600',
  },
});
